import classNames from 'classnames'
import { motion } from 'framer-motion'
import React, { DependencyList, PropsWithChildren, RefObject, useRef } from 'react'

import { useLayout } from '@app/routing/LayoutContext'

import { DummyIcon } from '@ui/icons/Dummy.icon'
import { Scrollbar } from '@ui/v2/Scrollbar/Scrollbar'

import { useWindowResize } from '@utils/domUtils'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import styles from './FullScreenLayout.module.scss'

interface FullScreenLayoutProps extends PropsWithChildren {
  maxContentWidth?: number
  isLoading?: boolean
  contentRef?: RefObject<HTMLDivElement | null>
  header?: React.ReactNode
  footer?: React.ReactNode
  headerLeftSlot?: React.ReactNode
  headerRightSlot?: React.ReactNode
  className?: string
  isContentScrollable?: boolean
}

export const FullScreenLayoutScrollerID = 'full-screen-layout-scroller'
export const useScrollFullScreenToTop = (effect?: DependencyList) =>
  useScrollToTop({ effect, id: FullScreenLayoutScrollerID })

export const FullScreenLayout: React.FC<FullScreenLayoutProps> = (props) => {
  const {
    isLoading,
    headerLeftSlot,
    headerRightSlot,
    children,
    maxContentWidth = 600,
    contentRef,
    header,
    className,
    isContentScrollable = true,
  } = props
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const { hasTopbar } = useLayout()

  const isMobile = useWindowResize()

  const renderSection = (children: React.ReactNode) => (
    <Box
      flexItem='0 0 auto'
      fullWidth
      zIndex='5'
      bg='surface-white-smoke-0'
      py='x0'
      px='x16'
      maxWidth={maxContentWidth ?? 600}
    >
      {children}
    </Box>
  )

  return (
    <Loading showLoadingIcon isLoading={isLoading ?? false}>
      <Box
        flex
        direction='column'
        align='center'
        gap={isMobile ? 'x16' : 'x32'}
        fullHeight
        fullWidth
        maxHeight='100dvh'
        overflowX='hidden'
        overflowY='hidden'
        className={classNames(styles.wrapper, className, {
          [styles.withMenu]: hasTopbar,
        })}
      >
        {(!!headerLeftSlot || !!headerRightSlot) && (
          <Box
            flex
            align={isMobile ? 'stretch' : 'center'}
            justify='space-between'
            fullWidth
            py='x12'
            px={isMobile ? 'x16' : 'x40'}
            zIndex='10'
            bg='surface-white-smoke-0'
            flexItem='0 0 auto'
            borderBottom='outline-base-em'
          >
            {headerLeftSlot ? headerLeftSlot : <DummyIcon />}
            {headerRightSlot ? headerRightSlot : <DummyIcon />}
          </Box>
        )}
        {!headerLeftSlot && !headerRightSlot && <Box flex fullWidth py='x32' />}
        {header && renderSection(header)}
        <Box flex fullWidth fullHeight flexItem='1 1 0' minHeight='0' position='relative'>
          <Box
            flex
            justify='center'
            flexItem='1 1 0%'
            minHeight='0'
            overflowY={isContentScrollable ? 'auto' : 'hidden'}
            id={FullScreenLayoutScrollerID}
            fullWidth
            className={classNames(styles.contentWrapper, {
              'custom-scrollbar-y-active': isContentScrollable,
              [styles.contentNotScrollable]: !isContentScrollable,
            })}
            ref={scrollRef}
          >
            <motion.div
              ref={contentRef}
              className={styles.content}
              style={
                {
                  maxWidth: maxContentWidth,
                  '--max-content-width': `${maxContentWidth}px`,
                } as React.CSSProperties
              }
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {children}
            </motion.div>
          </Box>
          {isContentScrollable && (
            <Scrollbar parentRef={scrollRef} size='sm' orientation='vertical' />
          )}
        </Box>
      </Box>
    </Loading>
  )
}
